import React from 'react';

import { Button } from '@xingternal/button';
import { IconCross, IconFile, IconFileImage, IconFilePDF, IconFileWord } from '@xingternal/icons';
import { List, ListItem } from '@xingternal/list';
import { ProgressBar } from '@xingternal/progress-bar';
import { BodyCopy, Meta } from '@xingternal/typography';

import { UploadedFile, UseUploadResult } from '@/domain/job-application/hooks';
import { formatBytes } from '@/helpers/format-bytes';

import styles from './uploads-list.css';
import lineClamp from '@/utils/line-clamp.css';

type Props = {
  className?: string;
  uploads: UploadedFile[];
  onDeleteUpload: UseUploadResult['handleDeleteUpload'];
};

const ICONS = {
  'text/plain': IconFileWord,
  'application/msword': IconFileWord,
  'application/vnd.oasis.opendocument.text': IconFileWord,
  'application/rtf': IconFileWord,
  'application/pdf': IconFilePDF,
  'image/jpeg': IconFileImage,
  'image/png': IconFileImage,
};

export const UploadsList: React.FC<Props> = ({ className, uploads, onDeleteUpload }) => (
  <List className={className}>
    {uploads.map((upload) => {
      const { id, fileName, fileType, fileSize, progress } = upload;
      const Icon = ICONS[fileType] ?? IconFile;

      return (
        <ListItem key={id} className={styles.listItem} variant="left">
          {progress !== 100 ? (
            <ProgressBar className={styles.progressBar} progress={progress} data-testid="uploadProgressBar" />
          ) : (
            <>
              <Icon className={styles.fileIcon} />

              <div className={styles.meta}>
                <BodyCopy fontWeight="bold" size="medium" noMargin className={lineClamp.lineClamp2}>
                  {fileName}
                </BodyCopy>
                <Meta size="small">{formatBytes(fileSize, 0)}</Meta>
              </div>
            </>
          )}

          <Button
            icon={IconCross}
            onClick={(): void => {
              onDeleteUpload(upload);
            }}
            data-testid="deleteUploadButton"
          />
        </ListItem>
      );
    })}
  </List>
);
